import { Injectable, effect, inject, signal } from '@angular/core';
import { doc, setDoc } from 'firebase/firestore';
import { ThemePreference } from '../../models/user-profile.type';
import { FirebaseAuthService } from './firebase-auth.service';
import { UserProfileService } from './user-profile.service';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { syncPwaThemeColor } from '../clinic-theme.service';

const DEFAULT_THEME: ThemePreference = 'light';

@Injectable()
export class ThemeService {
  private auth = inject(FirebaseAuthService);
  private profiles = inject(UserProfileService);
  private firestore = inject(FIREBASE_FIRESTORE);

  readonly preference = signal<ThemePreference>(DEFAULT_THEME);

  constructor() {
    // Start in light until a logged-in profile says otherwise.
    this.apply(DEFAULT_THEME);

    effect(() => {
      const loggedIn = this.auth.isLoggedIn();
      const ready = this.profiles.profileReady();
      const profile = this.profiles.profile();

      if (!loggedIn) {
        this.apply(DEFAULT_THEME);
        return;
      }

      if (!ready) {
        return;
      }

      this.apply(profile?.theme === 'dark' ? 'dark' : DEFAULT_THEME);
    });
  }

  async toggle(): Promise<void> {
    const next: ThemePreference = this.preference() === 'light' ? 'dark' : 'light';
    await this.setPreference(next);
  }

  async setPreference(mode: ThemePreference): Promise<void> {
    if (this.preference() === mode) {
      return;
    }
    this.apply(mode);

    const userId = this.auth.uniqueId();
    if (!userId) {
      return;
    }

    await setDoc(doc(this.firestore, 'users', userId), { theme: mode }, { merge: true });
    this.profiles.patchLocalProfile({ theme: mode });
  }

  private apply(mode: ThemePreference): void {
    this.preference.set(mode);
    const root = document.documentElement;
    root.classList.toggle('theme-light', mode === 'light');
    root.classList.toggle('theme-dark', mode === 'dark');
    root.style.colorScheme = mode;
    syncPwaThemeColor();
  }
}
